import { useCallback, useEffect, useMemo, useState } from 'react'
import styles from './GameReport.module.css'
import { formatTime } from '../../../../util/number'
import { t } from 'i18next'
import { ReportSortDir, TaskSortOption, useReportData } from '../../../../contexts/GameReportContextProvider'
import { CellElement, NO_POINTS_SKIP, Table, TableRow } from '../../../reports/components/GenericTable'
import { getIcon } from '../../../../common/components/icons/utils'
import { ExerciseType } from '../../../../api/gameTypes'

type TasksTabProps = {
  maxPoints?: number
  externalTasksSortDir?: ReportSortDir
  noPointsGame?: boolean
}

export const TasksTab: React.FC<TasksTabProps> = ({ maxPoints, externalTasksSortDir, noPointsGame }) => {
  const [activeTaskSortCol, setActiveTaskSortCol] = useState<TaskSortOption>()
  const { setTaskSort, tasksData, playerCount } = useReportData()

  useEffect(() => {
    if (externalTasksSortDir && setTaskSort) {
      setActiveTaskSortCol('averageScorePercentage')
      setTaskSort('averageScorePercentage', externalTasksSortDir)
    }
  }, [externalTasksSortDir, setTaskSort])

  const handleTaskSort = useCallback(
    (sortKey: string) => (direction: TaskSortOption | string) => {
      if (setTaskSort) {
        setTaskSort(sortKey as TaskSortOption, direction as ReportSortDir)
        setActiveTaskSortCol(sortKey as TaskSortOption)
      }
    },
    [setTaskSort],
  )

  const tasksTableRowData = useMemo((): TableRow[] => {
    const rows: TableRow[] = []

    tasksData?.forEach((row) => {
      const tableRow: CellElement[] = [
        <>
          <div>{row.name}</div>
          <div className={styles.playerSubDetailRow}>{row.type}</div>
        </>,
        formatTime(row.averageTime),
        row.completedCount,
        <div className={styles.evaluatedCell}>
          <div>{row.evaluatedCount + ' / ' + row.answerCount}</div>
          {row.evaluatedCount < row.answerCount && <span className={styles.alertIcon}>{getIcon('alert')}</span>}
        </div>,
        noPointsGame
          ? NO_POINTS_SKIP
          : row.rawType === ExerciseType.ExploreExercise
          ? '-'
          : (row.evaluatedCount > 0 ? row.averagePoints : '-') + ' / ' + row.maxPoints,
        noPointsGame
          ? NO_POINTS_SKIP
          : row.rawType === ExerciseType.ExploreExercise
          ? '-'
          : (row.evaluatedCount > 0 ? row.averageScorePercentage : '-') + ' % ',
      ]
      rows.push(tableRow)
    })

    return rows
  }, [noPointsGame, tasksData])

  return (
    <Table
      isSortable
      headerWidths={[
        '20%',
        '16%',
        '16%',
        '16%',
        noPointsGame ? NO_POINTS_SKIP : '16%',
        noPointsGame ? NO_POINTS_SKIP : '16%',
      ]}
      headerLabels={[
        t('game_summary.tasks_table.name', 'Task Name'),
        t('game_summary.tasks_table.average_time', 'Average time'),
        t('game_summary.tasks_table.completed', 'Completed'),
        t('game_summary.tasks_table.evaluated', 'Evaluated'),
        noPointsGame ? NO_POINTS_SKIP : t('game_summary.tasks_table.average_total_points', 'Avg / Max pts'),
        noPointsGame ? NO_POINTS_SKIP : t('game_summary.tasks_table.average_score_percentage', 'Avg Score %'),
      ]}
      headerSortTags={[
        'name',
        'averageTime',
        'completedCount',
        'evaluatedCount',
        noPointsGame ? NO_POINTS_SKIP : 'averagePoints',
        noPointsGame ? NO_POINTS_SKIP : 'averageScorePercentage',
      ]}
      onSort={handleTaskSort}
      activeSortCol={activeTaskSortCol}
      rows={tasksTableRowData}
      tableTitle={t('game_summary.tasks_table.table_title', 'Tasks')}
      subTitleRow1={t('game_summary.tasks_table.sub_title1', '%{amount} players', { amount: playerCount })}
      subTitleRow2={
        noPointsGame
          ? undefined
          : t('game_summary.tasks_table.sub_title2', 'Total points: %{points}', {
              points: maxPoints,
            })
      }
    />
  )
}
